import {
  onCleanup,
  createRoot,
  createEffect,
  onMount,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import {
  register,
  createContext,
  withProvider,
  withConsumer,
  compose,
} from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';
import { animate } from 'https://cdn.jsdelivr.net/npm/motion@10.16.2/+esm';

// Utility custom element functions

function getContext(element) {
  const elementContextSymbol = Object.getOwnPropertySymbols(element).find((s) => s.description === 'element-context');
  const contextSymbol = Object.getOwnPropertySymbols(element[elementContextSymbol]).find(
    (s) => s.description === 'context'
  );
  return element[elementContextSymbol][contextSymbol];
}

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

// Components
function AccordionBlock(props, { element }) {
  const [accordion, setAccordion] = getContext(element);
  const children = Array.from(element.children);
  const accordionItems = children.filter((child) => child.tagName === 'ACCORDION-ITEM');

  accordionItems.forEach((accordionItem, index) => {
    accordionItem.setAttribute('index', index + 1);
    if (accordionItem.getAttribute('is-expanded') !== 'true') return;
    props.activeIndex = index + 1;
  });

  createEffect(() => {
    element.setAttribute('active-index', accordion.activeIndex);
    setAccordion('activeIndex', props.activeIndex);
  });
}

// Components
function AccordionItem(props, { element, accordion }) {
  const [state, setState] = accordion;
  setState('items', props.index - 1, { isExpanded: props.isExpanded || false });

  createEffect(() => {
    setState('items', props.index - 1, { isExpanded: props.isExpanded || false });
    element.setAttribute('is-expanded', props.index === state.activeIndex);
    element.setAttribute('aria-expanded', props.index === state.activeIndex);
  });
}

function AccordionTrigger(props, { element, accordion }) {
  const [state, setAccordion] = accordion;
  const accordionItem = element.closest('accordion-item');

  onMount(() => {
    const handleClick = (e) => {
      e.preventDefault();
      const index = parseInt(accordionItem.getAttribute('index'));
      if (state.isAnimating) return;
      if (index === state.activeIndex) return setAccordion('activeIndex', -1);
      return setAccordion('activeIndex', index);
    };

    element.addEventListener('click', handleClick);

    onCleanup(() => {
      element.removeEventListener('click', handleClick);
    });
  });
}

function AccordionContent(props, { element, accordion }) {
  const [state, setState] = accordion;

  const hideElement = (element) => {
    element.style.cssText = `
      display: none;
      opacity: 0;
      visibility: hidden;
      max-height: 0px;
    `;
  };

  const showElement = (element) => {
    element.style.cssText = `
      display: block;
      opacity: 1;
      visibility: visible;
    `;
  };

  const getHeight = (el) => {
    el.style.maxHeight = 'max-content';
    const height = el.getBoundingClientRect().height;
    el.style.maxHeight = 0;
    return height;
  };

  const getPaddingTop = (element) => {
    const paddingY = parseInt(getComputedStyle(element).paddingTop);
    return paddingY;
  };

  const getPaddingBottom = (element) => {
    const paddingY = parseInt(getComputedStyle(element).paddingBottom);
    return paddingY;
  };

  const transition = { duration: 0.35 };

  const expand = (element) => {
    showElement(element);

    const height = getHeight(element),
      pt = getPaddingTop(element),
      pb = getPaddingBottom(element);

    return animate(
      element,
      { maxHeight: ['0px', `${height}px`], paddingTop: ['0px', `${pt}px`], paddingBottom: ['0px', `${pb}px`] },
      transition
    ).finished;
  };

  const collapse = (element) => {
    const height = getHeight(element),
      pt = getPaddingTop(element),
      pb = getPaddingBottom(element);

    return animate(
      element,
      { maxHeight: [`${height}px`, '0px'], paddingTop: [`${pt}px`, '0px'], paddingBottom: [`${pb}px`, '0px'] },
      transition
    ).finished.then(() => hideElement(element));
  };

  createEffect(() => {
    const accordionItem = element.closest('accordion-item');
    const index = parseInt(accordionItem.getAttribute('index'));
    const item = state.items[index - 1];
    if (!item) return;
    setState('isAnimating', true);
    if (!item.isExpanded) return collapse(element).then(() => setState('isAnimating', false));

    const isFirstRender = getComputedStyle(element).maxHeight === 'max-content';
    if (isFirstRender) {
      element.style.maxHeight = `${getHeight(element)}px`;
      return showElement(element);
    }
    return expand(element).then(() => setState('isAnimating', false));
  });
}

// Context
const AccordionContext = createContext((state = { activeIndex: 1, isAnimating: false, items: [] }) => {
  return createStore(state);
});

// Custom Element Definitions
customShadowlessElement('accordion-block', { activeIndex: -1 }, AccordionBlock, withProvider(AccordionContext));
customShadowlessElement(
  'accordion-item',
  { index: 0, isExpanded: false, ariaExpanded: false, tabindex: 0 },
  AccordionItem,
  withConsumer(AccordionContext, 'accordion')
);
customShadowlessElement('accordion-trigger', {}, AccordionTrigger, withConsumer(AccordionContext, 'accordion'));
customShadowlessElement('accordion-content', {}, AccordionContent, withConsumer(AccordionContext, 'accordion'));
